import type { MergeDeep } from 'type-fest';

import type { MsalProfile } from './types/coworker.types';

export type Valuable<T> = { [K in keyof T as T[K] extends null | undefined ? never : K]: T[K] };

export function invariant(expr: unknown, msg?: string): asserts expr {
    if (!expr) {
        throw new Error(msg);
    }
}

export function isMsalProfile(data: unknown): data is MsalProfile {
    return typeof data === 'object' && data !== null && 'msalProfile' in data;
}

export function isNumber(value: unknown): value is number {
    return typeof value === 'number';
}

export function isString(value: unknown): value is string {
    return typeof value === 'string';
}

export const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

export function filterNonNullProperties<T extends object>(obj: T): Valuable<T> {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return Object.fromEntries(
        Object.entries(obj).filter(
            ([, v]) => !((typeof v === 'string' && v.length === 0) || v === null || typeof v === 'undefined')
        )
    ) as Valuable<T>;
}

export function isDefined<T>(value: T | undefined | null): value is T {
    return value !== undefined && value !== null;
}

export function hasItems<T>(value?: T[]): value is T[] {
    return isDefined(value) && value.length > 0;
}

export function isEmpty(value: unknown): value is never {
    if (isString(value) || Array.isArray(value)) {
        return value.length === 0;
    }
    if (isObject(value)) {
        return Object.keys(value).length === 0;
    }
    return value === undefined || value === null;
}

export const hasProperty = <T extends Record<string | number | symbol, unknown>>(
    obj: T,
    prop: unknown
): prop is keyof T => {
    if (typeof prop !== 'string' && typeof prop !== 'number' && typeof prop !== 'symbol') {
        return false;
    }

    return Object.prototype.hasOwnProperty.call(obj, prop);
};

export const mergeDeep = <T, S>(target: T, source: S): MergeDeep<T, S> => {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    const output = Object.assign({}, target) as MergeDeep<T, S>;

    if (isObject(target) && isObject(source)) {
        for (const [key, sourceValue] of Object.entries(source)) {
            if (isObject(sourceValue)) {
                if (key in target) {
                    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
                    (output as Record<string, unknown>)[key] = mergeDeep(target[key], sourceValue);
                } else {
                    // oxlint-disable-next-line typescript/no-floating-promises
                    Object.assign(output, { [key]: sourceValue });
                }
            } else {
                // oxlint-disable-next-line typescript/no-floating-promises
                Object.assign(output, { [key]: sourceValue });
            }
        }
    }

    return output;
};
